import { calculateCardCompletion } from "@shared/field-definitions";
import type { WizardSlice } from "./types";
import type {
  GeneralInfo,
  PolicyTopicData,
  DocumentMetadata,
} from "@/contexts/wizard/types";

/**
 * General slice state (general info + policy topics)
 */
export interface GeneralSliceState {
  generalInfo: GeneralInfo;
  descriptionOfActivities: string;
  hasSustainabilityPolicies: "yes" | "no" | null;
  sustainabilityPolicyDescription: string;
  sustainabilityPolicyDocuments: DocumentMetadata[];
  policyTopics: PolicyTopicData[];
  suggestionsDismissed: boolean;
}

/**
 * API response for general information (now includes sustainability data)
 */
export interface GeneralInfoApiData {
  companyName?: string;
  reportingCurrency?: string;
  totalEmployees?: number | null;
  maleEmployees?: number | null;
  femaleEmployees?: number | null;
  homeworkingPercent?: number | null;
  ownershipStructure?: string;
  legalForm?: string;
  naceSector?: string;
  descriptionOfActivities?: string;
  additionalInfo?: {
    companyLogoUrl?: string | null;
    annualRevenueRange?: string;
    numberOfSites?: string;
    siteAddresses?: Array<{ id: string; address: string; coords: { lat: number; lng: number } | null; isPrimary: boolean }>;
    legalFormSubtype?: string;
    netTurnover?: string;
    balanceSheetTotal?: string;
    sustainabilityCertifications?: string[];
    certificationDocuments?: DocumentMetadata[];
    financialStatementsDocuments?: DocumentMetadata[];
    employeeListDocuments?: DocumentMetadata[];
    // Sustainability gating & policy fields
    hasSustainabilityPolicies?: "yes" | "no" | null;
    sustainabilityPolicyDescription?: string;
    sustainabilityPolicyDocuments?: DocumentMetadata[];
  };
  // Sustainability fields (merged from sustainability_transition_responses)
  policiesByTopic?: PolicyTopicData[] | null;
  suggestionsDismissed?: boolean;
  generalInfoCompletenessPercent?: number;
  sustainabilityCompletenessPercent?: number;
}

/**
 * Combined API data for general slice (single endpoint)
 */
export interface GeneralApiData {
  generalInfo: GeneralInfoApiData;
}

/**
 * State setters for general slice
 */
export interface GeneralStateSetter {
  setGeneralInfo: (info: GeneralInfo) => void;
  setDescriptionOfActivities: (value: string) => void;
  setHasSustainabilityPolicies: (value: "yes" | "no" | null) => void;
  setSustainabilityPolicyDescription: (value: string) => void;
  setSustainabilityPolicyDocuments: (docs: DocumentMetadata[]) => void;
  setPolicyTopics: (topics: PolicyTopicData[]) => void;
  setSuggestionsDismissed: (dismissed: boolean) => void;
}

// ==================== API FUNCTIONS ====================

async function fetchGeneralInformation(assessmentId: string): Promise<GeneralInfoApiData> {
  const response = await fetch(`/api/assessments/${assessmentId}/general-information`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch general information");
  return response.json();
}

async function saveGeneralInformation(assessmentId: string, payload: unknown) {
  return fetch(`/api/assessments/${assessmentId}/general-information`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
}

async function patchAssessment(assessmentId: string, payload: unknown) {
  return fetch(`/api/assessments/${assessmentId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
}

// ==================== HYDRATION MAPPER ====================

function hydrateGeneralState(apiData: GeneralApiData, setter: GeneralStateSetter): void {
  const generalData = apiData.generalInfo;

  const generalInfo: GeneralInfo = {
    companyName: generalData.companyName || "",
    companyLogoUrl: generalData.additionalInfo?.companyLogoUrl || null,
    reportingCurrency: generalData.reportingCurrency || "USD",
    annualRevenueRange: generalData.additionalInfo?.annualRevenueRange || "",
    totalEmployees: generalData.totalEmployees?.toString() || "",
    maleEmployees: generalData.maleEmployees?.toString() || "",
    femaleEmployees: generalData.femaleEmployees?.toString() || "",
    homeworkingPercent: generalData.homeworkingPercent?.toString() || "",
    numberOfSites: (generalData.additionalInfo?.numberOfSites as "1" | "2" | "3" | "4" | "5+") || "1",
    siteAddresses: generalData.additionalInfo?.siteAddresses || [{ id: "site-1", address: "", coords: null, isPrimary: true }],
    ownershipStructure: generalData.ownershipStructure || "",
    legalForm: generalData.legalForm || "",
    legalFormSubtype: generalData.additionalInfo?.legalFormSubtype || "",
    naceSector: generalData.naceSector || "",
    netTurnover: generalData.additionalInfo?.netTurnover || "",
    balanceSheetTotal: generalData.additionalInfo?.balanceSheetTotal || "",
    sustainabilityCertifications: generalData.additionalInfo?.sustainabilityCertifications || [],
    certificationDocuments: generalData.additionalInfo?.certificationDocuments || [],
    financialStatementsDocuments: generalData.additionalInfo?.financialStatementsDocuments || [],
    employeeListDocuments: generalData.additionalInfo?.employeeListDocuments || [],
  };

  setter.setGeneralInfo(generalInfo);

  // Sustainability: descriptionOfActivities (same DB column, now standalone state)
  setter.setDescriptionOfActivities(generalData.descriptionOfActivities || "");

  // Policy topics
  const policyTopics = generalData.policiesByTopic && generalData.policiesByTopic.length > 0
    ? generalData.policiesByTopic
    : [];
  setter.setPolicyTopics(policyTopics);

  // Sustainability gating & policy fields (from additionalInfo, with backward-compat migration)
  const additionalInfo = generalData.additionalInfo as Record<string, any> | undefined;

  // Backward-compat: infer hasSustainabilityPolicies from existing topic data
  let hasPolicies = additionalInfo?.hasSustainabilityPolicies ?? null;
  if (hasPolicies === null && policyTopics.some((t: PolicyTopicData) => t.policyExists !== null)) {
    hasPolicies = policyTopics.some((t: PolicyTopicData) => t.policyExists === "yes") ? "yes" : "no";
  }
  setter.setHasSustainabilityPolicies(hasPolicies);

  setter.setSustainabilityPolicyDescription(additionalInfo?.sustainabilityPolicyDescription ?? "");
  setter.setSustainabilityPolicyDocuments(additionalInfo?.sustainabilityPolicyDocuments ?? []);

  setter.setSuggestionsDismissed(generalData.suggestionsDismissed || false);
}

// ==================== SIGNATURE & DIRTY TRACKING ====================

function calculateGeneralInfoCompletion(state: GeneralSliceState): number {
  return calculateCardCompletion('generalInfo', state.generalInfo);
}

function calculateSustainabilityCompletion(state: GeneralSliceState): number {
  // For this card, only descriptionOfActivities is currently required.
  // Optional fields are included so future required-field changes stay aligned.
  return calculateCardCompletion("sustainability", {
    descriptionOfActivities: state.descriptionOfActivities,
    sustainabilityCertifications: state.generalInfo.sustainabilityCertifications,
    policiesByTopic: state.policyTopics,
  });
}

function computeGeneralSignature(state: GeneralSliceState): string {
  const fte = parseFloat(state.generalInfo.totalEmployees) || 0;

  const assessmentPayload = {
    organizationName: state.generalInfo.companyName || "Unnamed Organization",
    employeeCount: fte > 0 ? Math.round(fte) : null,
  };

  const additionalInfo = {
    companyLogoUrl: state.generalInfo.companyLogoUrl,
    annualRevenueRange: state.generalInfo.annualRevenueRange,
    numberOfSites: state.generalInfo.numberOfSites,
    siteAddresses: state.generalInfo.siteAddresses,
    legalFormSubtype: state.generalInfo.legalFormSubtype,
    netTurnover: state.generalInfo.netTurnover,
    balanceSheetTotal: state.generalInfo.balanceSheetTotal,
    sustainabilityCertifications: state.generalInfo.sustainabilityCertifications,
    certificationDocuments: state.generalInfo.certificationDocuments,
    financialStatementsDocuments: state.generalInfo.financialStatementsDocuments,
    employeeListDocuments: state.generalInfo.employeeListDocuments,
    // Sustainability gating & policy fields
    hasSustainabilityPolicies: state.hasSustainabilityPolicies,
    sustainabilityPolicyDescription: state.sustainabilityPolicyDescription,
    sustainabilityPolicyDocuments: state.sustainabilityPolicyDocuments,
  };

  const sustainabilityCompletion = calculateSustainabilityCompletion(state);

  const generalInfoPayload = {
    companyName: state.generalInfo.companyName || "Unnamed Organization",
    reportingCurrency: state.generalInfo.reportingCurrency || "USD",
    totalEmployees: fte > 0 ? Math.round(fte) : null,
    maleEmployees:
      (state.generalInfo.maleEmployees?.trim() || "") !== "" ? parseInt(state.generalInfo.maleEmployees) : null,
    femaleEmployees:
      (state.generalInfo.femaleEmployees?.trim() || "") !== "" ? parseInt(state.generalInfo.femaleEmployees) : null,
    homeworkingPercent:
      (state.generalInfo.homeworkingPercent?.trim() || "") !== ""
        ? parseInt(state.generalInfo.homeworkingPercent)
        : null,
    ownershipStructure: state.generalInfo.ownershipStructure || null,
    legalForm: state.generalInfo.legalForm || null,
    naceSector: state.generalInfo.naceSector || null,
    descriptionOfActivities: state.descriptionOfActivities || null,
    additionalInfo: additionalInfo,
    policiesByTopic: state.policyTopics,
    suggestionsDismissed: state.suggestionsDismissed,
    generalInfoCompletenessPercent: calculateGeneralInfoCompletion(state),
    sustainabilityCompletenessPercent: sustainabilityCompletion,
  };

  return JSON.stringify({
    assessment: assessmentPayload,
    generalInfo: generalInfoPayload,
  });
}

function isGeneralDirty(state: GeneralSliceState, baselineSignature: string): boolean {
  return computeGeneralSignature(state) !== baselineSignature;
}

// ==================== SAVE ====================

async function saveGeneralSlice(assessmentId: string, state: GeneralSliceState): Promise<void> {
  const fte = parseFloat(state.generalInfo.totalEmployees) || 0;

  // Save assessment base fields
  const assessmentPayload = {
    organizationName: state.generalInfo.companyName || "Unnamed Organization",
    employeeCount: fte > 0 ? Math.round(fte) : null,
  };
  const assessmentResponse = await patchAssessment(assessmentId, assessmentPayload);
  if (!assessmentResponse.ok) {
    const error = await assessmentResponse.json().catch(() => ({ error: "Failed to save assessment" }));
    throw new Error(error.error || "Failed to save assessment");
  }

  // Save general information + sustainability data in single call
  const additionalInfo = {
    companyLogoUrl: state.generalInfo.companyLogoUrl,
    annualRevenueRange: state.generalInfo.annualRevenueRange,
    numberOfSites: state.generalInfo.numberOfSites,
    siteAddresses: state.generalInfo.siteAddresses,
    legalFormSubtype: state.generalInfo.legalFormSubtype,
    netTurnover: state.generalInfo.netTurnover,
    balanceSheetTotal: state.generalInfo.balanceSheetTotal,
    sustainabilityCertifications: state.generalInfo.sustainabilityCertifications,
    certificationDocuments: state.generalInfo.certificationDocuments,
    financialStatementsDocuments: state.generalInfo.financialStatementsDocuments,
    employeeListDocuments: state.generalInfo.employeeListDocuments,
    // Sustainability gating & policy fields
    hasSustainabilityPolicies: state.hasSustainabilityPolicies,
    sustainabilityPolicyDescription: state.sustainabilityPolicyDescription,
    sustainabilityPolicyDocuments: state.sustainabilityPolicyDocuments,
  };

  const sustainabilityCompletion = calculateSustainabilityCompletion(state);

  const generalInfoPayload = {
    companyName: state.generalInfo.companyName || "Unnamed Organization",
    reportingCurrency: state.generalInfo.reportingCurrency || "USD",
    totalEmployees: fte > 0 ? Math.round(fte) : null,
    maleEmployees:
      (state.generalInfo.maleEmployees?.trim() || "") !== "" ? parseInt(state.generalInfo.maleEmployees) : null,
    femaleEmployees:
      (state.generalInfo.femaleEmployees?.trim() || "") !== "" ? parseInt(state.generalInfo.femaleEmployees) : null,
    homeworkingPercent:
      (state.generalInfo.homeworkingPercent?.trim() || "") !== ""
        ? parseInt(state.generalInfo.homeworkingPercent)
        : null,
    ownershipStructure: state.generalInfo.ownershipStructure || null,
    legalForm: state.generalInfo.legalForm || null,
    naceSector: state.generalInfo.naceSector || null,
    descriptionOfActivities: state.descriptionOfActivities || null,
    additionalInfo: additionalInfo,
    policiesByTopic: state.policyTopics,
    suggestionsDismissed: state.suggestionsDismissed,
    generalInfoCompletenessPercent: calculateGeneralInfoCompletion(state),
    sustainabilityCompletenessPercent: sustainabilityCompletion,
  };

  const generalInfoResponse = await saveGeneralInformation(assessmentId, generalInfoPayload);
  if (!generalInfoResponse.ok) {
    const error = await generalInfoResponse.json().catch(() => ({ error: "Failed to save general information" }));
    throw new Error(error.error || "Failed to save general information");
  }
}

// ==================== COMPLETION DATA ====================

function getGeneralCompletionData(state: GeneralSliceState) {
  return {
    generalInfo: state.generalInfo,
    policyTopics: state.policyTopics,
  };
}

// ==================== SLICE EXPORT ====================

async function fetchGeneralSliceData(assessmentId: string): Promise<GeneralApiData> {
  const generalInfo = await fetchGeneralInformation(assessmentId);
  return { generalInfo };
}

export const generalSlice: WizardSlice<GeneralSliceState, GeneralApiData, GeneralStateSetter> = {
  queryKey: (assessmentId: string) => ["general", assessmentId],
  fetch: fetchGeneralSliceData,
  hydrate: hydrateGeneralState,
  isDirty: isGeneralDirty,
  computeSignature: computeGeneralSignature,
  save: saveGeneralSlice,
  getCompletionData: getGeneralCompletionData,
  impactsCompletion: true,
};
